import { Head, Link } from '@inertiajs/react';
import { EditorRenderer } from '@/components/Editor';

/**
 * Article data from the backend.
 */
interface NewspaperArticle {
    id: number;
    issue_number: number;
    title: string;
    slug: string;
    byline: string;
    content: string;
    cover_image_url: string | null;
    is_published: boolean;
    published_at: string | null;
}

/**
 * Navigation article (prev/next).
 */
interface NavArticle {
    issue_number: number;
    title: string;
    slug: string;
}

/**
 * Props for the Article page.
 */
interface Props {
    article: NewspaperArticle;
    prevArticle: NavArticle | null;
    nextArticle: NavArticle | null;
    isGm: boolean;
}

/**
 * Public Newspaper Article page - displays a single article.
 * Uses traditional newspaper-style typography with white background and black text.
 */
export default function Article({ article, prevArticle, nextArticle, isGm }: Props) {
    return (
        <>
            <Head title={`${article.title} - The Feywild Times`} />

            <div className="min-h-screen bg-gray-50">
                {/* Newspaper Header */}
                <header className="bg-white border-b-4 border-double border-gray-900 py-4">
                    <div className="max-w-4xl mx-auto px-4 text-center">
                        <p className="text-sm text-gray-600 font-serif mb-1">
                            Issue #{article.issue_number}
                        </p>
                        <Link href="/newspaper" className="hover:opacity-80 transition-opacity">
                            <h1 className="text-3xl md:text-4xl font-serif font-bold text-black tracking-tight">
                                The Feywild Times
                            </h1>
                        </Link>
                    </div>
                </header>

                {/* Article Content */}
                <main className="max-w-4xl mx-auto px-4 py-6">
                    {/* Draft indicator */}
                    {isGm && !article.is_published && (
                        <div className="mb-4 px-4 py-2 bg-yellow-100 border border-yellow-400 rounded text-yellow-800 text-sm">
                            ⚠️ This article is a draft and not visible to regular users.
                            <Link href={`/gm/newspaper/${article.id}/edit`} className="ml-2 underline">
                                Edit in GM Dashboard
                            </Link>
                        </div>
                    )}

                    {/* Cover Image */}
                    {article.cover_image_url && (
                        <img
                            src={article.cover_image_url}
                            alt={article.title}
                            className="w-full h-64 md:h-96 object-cover rounded-lg mb-6"
                        />
                    )}

                    {/* Article Header */}
                    <div className="mb-8 text-center border-b border-gray-300 pb-6">
                        <div className="text-sm text-gray-600 mb-2">
                            {article.published_at && (
                                <>{new Date(article.published_at).toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                })}</>
                            )}
                        </div>
                        <h1 className="text-3xl md:text-5xl font-serif font-bold text-black mb-4 leading-tight">
                            {article.title}
                        </h1>
                        <p className="text-gray-600 font-serif italic">
                            By {article.byline}
                        </p>
                    </div>

                    {/* Article Body */}
                    <article className="prose prose-lg max-w-none font-serif">
                        <div className="text-gray-900 leading-relaxed first-letter:text-5xl first-letter:font-bold first-letter:float-left first-letter:mr-2 first-letter:mt-1">
                            <EditorRenderer content={article.content} theme="light" />
                        </div>
                    </article>

                    {/* Navigation */}
                    <nav className="mt-12 pt-6 border-t border-gray-300">
                        <div className="flex justify-between items-center">
                            {prevArticle ? (
                                <Link
                                    href={`/newspaper/${prevArticle.slug}`}
                                    className="flex flex-col items-start text-gray-700 hover:text-gray-500 transition-colors"
                                >
                                    <span className="text-xs text-gray-600">← Previous</span>
                                    <span className="font-serif font-medium line-clamp-1">
                                        {prevArticle.title}
                                    </span>
                                </Link>
                            ) : (
                                <div />
                            )}

                            <Link
                                href="/newspaper"
                                className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 transition-colors font-serif"
                            >
                                All Issues
                            </Link>

                            {nextArticle ? (
                                <Link
                                    href={`/newspaper/${nextArticle.slug}`}
                                    className="flex flex-col items-end text-gray-700 hover:text-gray-500 transition-colors"
                                >
                                    <span className="text-xs text-gray-600">Next →</span>
                                    <span className="font-serif font-medium line-clamp-1">
                                        {nextArticle.title}
                                    </span>
                                </Link>
                            ) : (
                                <div />
                            )}
                        </div>
                    </nav>
                </main>
            </div>
        </>
    );
}

